// src/hooks.js
import { useEffect, useState, useRef, RefObject } from 'react';

export function useHeadsObserver(contentRef?: RefObject<HTMLElement>) {
    const observer = useRef<IntersectionObserver | null>(null)
    const [activeId, setActiveId] = useState('')
    // Keep track of observed elements to avoid duplicate observations if possible,
    // though IntersectionObserver.observe is idempotent for the same element.
    const observedElements = useRef<Set<Element>>(new Set())
    const intersectingEntries = useRef<Map<string, boolean>>(new Map())

    useEffect(() => {
        const handleObserver = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                intersectingEntries.current.set(entry.target.id, entry.isIntersecting)
            })

            // Determine which ID should be active
            // We want the visible header that is closest to the top
            const visibleIds: string[] = []
            intersectingEntries.current.forEach((value, key) => {
                if (value) visibleIds.push(key)
            })

            if (visibleIds.length > 0) {
                // Find element with smallest positive top position
                const elements = visibleIds.map(id => document.getElementById(id)).filter(e => e !== null) as HTMLElement[]

                if (elements.length > 0) {
                    // Sort by vertical position (top to bottom)
                    elements.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)

                    // The first one is the topmost visible element
                    setActiveId(elements[0].id)
                }
            }
        }

        // Use viewport as root but respect the scrolling container's context if needed.
        // If the scrolling happens within contentRef, using null (viewport) usually works 
        // provided the container intersects the viewport. 
        observer.current = new IntersectionObserver(handleObserver, {
            rootMargin: "-20% 0% -35% 0px"
        })

        const observeElements = () => {
            // If a ref is provided, search within it. Otherwise search document.
            const root = contentRef?.current || document;
            const elements = root.querySelectorAll("h1[id], h2[id], h3[id], h4[id]")

            elements.forEach((elem) => {
                // Ensure we observe valid elements
                if (elem && !observedElements.current.has(elem)) {
                    observer.current?.observe(elem)
                    observedElements.current.add(elem)
                }
            })
        }

        // Initial observation
        observeElements()

        // Setup MutationObserver to handle content changes (e.g. Markdown rendering)
        const mutationObserver = new MutationObserver(observeElements)

        if (contentRef?.current) {
            mutationObserver.observe(contentRef.current, {
                childList: true,
                subtree: true
            })
        } else {
            // Fallback: simple timeout if no ref provided (legacy behavior)
            setTimeout(observeElements, 500)
        }

        return () => {
            observer.current?.disconnect()
            mutationObserver.disconnect()
            observedElements.current.clear()
        }
    }, [contentRef]) // Re-run if contentRef changes (though it usually doesn't)

    return { activeId }
}